import { Injectable, inject } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { ApiResponse } from '../config/api.config';
import { CustomerPayment } from '../models/customer-payment.model';
import { ApiStore, newestFirst } from './api-store';
import { SalesOrderService } from './sales-order.service';

/** Fields of a payment that can be edited after it was recorded. */
export interface CustomerPaymentDetailsPatch {
  paymentDate?: string;
  method?: CustomerPayment['method'];
  referenceNumber?: string | null;
  notes?: string | null;
}

export type CreateCustomerPaymentInput = Omit<CustomerPayment, 'id' | 'status'> & Partial<Pick<CustomerPayment, 'status'>>;

/**
 * Payments received from customers (/customer-payments). Only 'Succeeded'
 * payments settle invoices; the API updates the sales orders' payment status.
 */
@Injectable({ providedIn: 'root' })
export class CustomerPaymentService extends ApiStore<CustomerPayment> {
  protected readonly resource = 'customer-payments';
  protected override readonly order = newestFirst<CustomerPayment>((p) => p.paymentDate);
  protected override readonly readAbility = 'read.customerPayments' as const;
  private readonly salesOrderService = inject(SalesOrderService);

  create(input: CreateCustomerPaymentInput): Observable<CustomerPayment> {
    return this.save(
      this.http.post<ApiResponse<CustomerPayment>>(this.url, input).pipe(tap(() => this.salesOrderService.refresh())),
    );
  }

  setStatus(id: string, status: CustomerPayment['status']): Observable<CustomerPayment> {
    return this.save(
      this.http
        .patch<ApiResponse<CustomerPayment>>(`${this.url}/${id}/status`, { status })
        .pipe(tap(() => this.salesOrderService.refresh())),
    );
  }

  /** "Edit payment details" — date, method, reference number and notes; amounts don't change. */
  update(id: string, patch: CustomerPaymentDetailsPatch): Observable<CustomerPayment> {
    return this.save(this.http.put<ApiResponse<CustomerPayment>>(`${this.url}/${id}`, patch));
  }

  /** "Undo payment" — deletes the payment, releasing its applied amounts. */
  remove(id: string): Observable<unknown> {
    this.removeLocal(id);
    return this.send(this.http.delete(`${this.url}/${id}`).pipe(tap(() => this.salesOrderService.refresh())));
  }
}
